/**
 * InfluencerCampaignDetail.jsx — the workhorse for a single campaign.
 * Tabs: Overview · Creators · Content · Metrics · Payments
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import {
  ChevronLeft, Plus, Users2, Camera, BarChart3, IndianRupee, Loader2,
  Sparkles, Check, X, RotateCw, ExternalLink, Wand2, FileText, FileSignature,
  Copy, Download,
} from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

const PARTICIPANT_STATUSES = ["invited", "negotiating", "agreed", "briefed", "content_review", "posted", "measured", "paid", "dropped"];
const PART_TONE = {
  invited: "bg-neutral-100 text-neutral-700",
  negotiating: "bg-sky-50 text-sky-700",
  agreed: "bg-emerald-50 text-emerald-700",
  briefed: "bg-indigo-50 text-indigo-700",
  content_review: "bg-amber-50 text-amber-700",
  posted: "bg-primary/10 text-primary",
  measured: "bg-violet-50 text-violet-700",
  paid: "bg-emerald-100 text-emerald-800",
  dropped: "bg-rose-50 text-rose-700",
};

export default function InfluencerCampaignDetail() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [summary, setSummary] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState("overview");
  const [showAddPart, setShowAddPart] = useState(false);
  const [showAddPay, setShowAddPay] = useState(null); // participant object
  const [showAddSub, setShowAddSub] = useState(null);
  const [showAddMetric, setShowAddMetric] = useState(null);
  const [showAiBrief, setShowAiBrief] = useState(false);
  const [showContract, setShowContract] = useState(null); // participant
  const [reportBusy, setReportBusy] = useState(false);
  const [deltas, setDeltas] = useState({ rows: [], blended: {} });

  const load = useCallback(async () => {
    try {
      const [c, s, parts, subs, pays, delt] = await Promise.all([
        api.get(`/influencer-campaigns/${cid}`),
        api.get(`/influencer-campaigns/${cid}/summary`),
        api.get(`/influencer-campaigns/${cid}/participants`),
        api.get(`/influencer-campaigns/${cid}/submissions`),
        api.get(`/influencer-campaigns/${cid}/payments`),
        api.get(`/influencer-campaigns/${cid}/participant-deltas`).catch(() => ({ data: { rows: [], blended: {} } })),
      ]);
      setCampaign(c.data);
      setSummary(s.data);
      setParticipants(parts.data?.rows || []);
      setSubmissions(subs.data?.rows || []);
      setPayments(pays.data?.rows || []);
      setDeltas(delt.data || { rows: [], blended: {} });
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  const setCampStatus = async (s) => {
    try {
      await api.patch(`/influencer-campaigns/${cid}`, { status: s });
      toast.success(`Status: ${s}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  const setPartStatus = async (pid, s) => {
    try {
      await api.patch(`/influencer-participants/${pid}`, { status: s });
      toast.success(`Status: ${s}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  const reviewSub = async (sid, status) => {
    try {
      await api.patch(`/influencer-submissions/${sid}/review`, { status });
      toast.success(`Content ${status}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Review failed")); }
  };

  const removePart = async (pid) => {
    if (!window.confirm("Remove this creator from the campaign?")) return;
    try {
      await api.delete(`/influencer-participants/${pid}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const downloadReport = async () => {
    setReportBusy(true);
    try {
      const res = await api.get(`/influencer-campaigns/${cid}/report`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${campaign?.code || "campaign"}-report.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) { toast.error(apiErrorMessage(e, "PDF failed")); }
    finally { setReportBusy(false); }
  };

  const deltaMap = useMemo(
    () => Object.fromEntries((deltas.rows || []).map((r) => [r.participant_id, r])),
    [deltas],
  );

  if (!campaign) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  const s = summary || {};
  const sp = s.spend || {};
  const perf = s.performance || {};
  const funnel = s.pipeline || {};

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="inf-camp-detail">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => nav("/app/influencers/campaigns")} className="text-muted-foreground hover:text-foreground text-xs inline-flex items-center gap-1" data-testid="inf-camp-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Campaigns
        </button>
        <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">{campaign.code}</div>
        <h1 className="text-2xl font-bold flex-1 min-w-0 truncate">{campaign.name}</h1>
        <Select value={campaign.status} onValueChange={setCampStatus}>
          <SelectTrigger className="h-8 text-xs w-40" data-testid="inf-camp-status-picker"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["draft", "planning", "active", "in_review", "completed", "archived", "cancelled"].map((s) =>
              <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
            )}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={() => setShowAiBrief(true)} data-testid="inf-camp-ai-brief">
          <Wand2 className="w-3.5 h-3.5 mr-1.5" /> AI brief
        </Button>
        <Button size="sm" variant="outline" onClick={downloadReport} disabled={reportBusy} data-testid="inf-camp-report">
          {reportBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}Client PDF
        </Button>
      </div>

      {campaign.brand?.name && (
        <div className="text-xs text-muted-foreground">Brand: <span className="text-foreground font-medium">{campaign.brand.name}</span></div>
      )}
      {campaign.brief && <div className="text-xs text-muted-foreground max-w-3xl whitespace-pre-wrap">{campaign.brief}</div>}

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="inf-camp-kpis">
        <Kpi label="Creators" value={campaign.participant_count || 0} />
        <Kpi label="Budget" value={`₹${((campaign.budget_total_inr || 0) / 1000).toFixed(0)}K`} />
        <Kpi label="Agreed spend" value={`₹${((sp.agreed_inr || 0) / 1000).toFixed(0)}K`} />
        <Kpi label="Paid" value={`₹${((sp.paid_inr || 0) / 1000).toFixed(0)}K`} tone={sp.paid_inr >= sp.agreed_inr && sp.agreed_inr > 0 ? "pos" : ""} />
        <Kpi label="Total reach" value={(perf.reach || 0).toLocaleString("en-IN")} />
      </div>

      {/* Tabs */}
      <div className="border-b border-border flex items-center gap-1 flex-wrap">
        {[
          { k: "overview", label: "Overview", icon: BarChart3 },
          { k: "creators", label: `Creators (${participants.length})`, icon: Users2 },
          { k: "content", label: `Content (${submissions.length})`, icon: Camera },
          { k: "metrics", label: "Metrics", icon: Sparkles },
          { k: "payments", label: `Payments (${payments.length})`, icon: IndianRupee },
        ].map((t) => (
          <button key={t.k} type="button" onClick={() => setTab(t.k)}
                  className={`text-xs px-3 py-2 border-b-2 -mb-px flex items-center gap-1 ${tab === t.k ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                  data-testid={`inf-camp-tab-${t.k}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-border p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Pipeline funnel</div>
            <div className="space-y-1.5">
              {PARTICIPANT_STATUSES.map((s) => {
                const n = funnel[s] || 0;
                const total = Object.values(funnel).reduce((a, b) => a + b, 0);
                const pct = total ? (n / total) * 100 : 0;
                return (
                  <div key={s} className="text-xs">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="uppercase text-[10px] tracking-wide">{s.replace("_", " ")}</span>
                      <span className="tabular-nums font-mono">{n}</span>
                    </div>
                    <div className="h-1.5 bg-muted"><div className="h-full bg-primary" style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="border border-border p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Performance</div>
            <dl className="grid grid-cols-2 gap-3 text-xs">
              <div><dt className="text-[10px] uppercase text-muted-foreground">Impressions</dt><dd className="font-mono text-lg">{(perf.impressions || 0).toLocaleString("en-IN")}</dd></div>
              <div><dt className="text-[10px] uppercase text-muted-foreground">Interactions</dt><dd className="font-mono text-lg">{(perf.interactions || 0).toLocaleString("en-IN")}</dd></div>
              <div><dt className="text-[10px] uppercase text-muted-foreground">Blended ER</dt><dd className="font-mono text-lg">{perf.blended_er_pct || 0}%</dd></div>
              <div><dt className="text-[10px] uppercase text-muted-foreground">Posts measured</dt><dd className="font-mono text-lg">{perf.post_count || 0}</dd></div>
              <div><dt className="text-[10px] uppercase text-muted-foreground">CPM</dt><dd className="font-mono text-lg">₹{perf.cpm_inr || 0}</dd></div>
              <div><dt className="text-[10px] uppercase text-muted-foreground">CPE</dt><dd className="font-mono text-lg">₹{perf.cpe_inr || 0}</dd></div>
            </dl>
          </div>
        </div>
      )}

      {tab === "creators" && (
        <div className="space-y-3" data-testid="inf-camp-creators-tab">
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setShowAddPart(true)} data-testid="inf-camp-add-creator">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add creator
            </Button>
          </div>
          {participants.length === 0 ? (
            <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground">
              No creators yet. Click "Add creator" to invite someone from your directory.
            </div>
          ) : (
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 border-b border-border">
                  <tr className="text-left text-[10px] uppercase text-muted-foreground">
                    <th className="px-3 py-2">Creator</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2 text-right">Agreed cost</th>
                    <th className="px-3 py-2 text-right">Paid</th>
                    <th className="px-3 py-2">Payment</th>
                    <th className="px-3 py-2 w-64">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {participants.map((p) => {
                    const inf = p.influencer || {};
                    const remaining = Math.max(0, (p.agreed_cost_inr || 0) - (p.paid_amount_inr || 0));
                    return (
                      <tr key={p.id} className="hover:bg-muted/20" data-testid={`inf-part-${p.id}`}>
                        <td className="px-3 py-2">
                          <Link to={`/app/influencers/${p.influencer_id}`} className="flex items-center gap-2 hover:text-primary">
                            <div className="w-7 h-7 bg-secondary flex items-center justify-center text-[10px] font-bold">{inf.name?.[0]?.toUpperCase()}</div>
                            <div className="min-w-0"><div className="font-medium truncate">{inf.name || "(deleted)"}</div><div className="text-[10px] text-muted-foreground">@{inf.primary_handle}</div></div>
                          </Link>
                        </td>
                        <td className="px-3 py-2">
                          <Select value={p.status} onValueChange={(v) => setPartStatus(p.id, v)}>
                            <SelectTrigger className={`h-7 text-[10px] uppercase ${PART_TONE[p.status] || ""}`} data-testid={`inf-part-status-${p.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {PARTICIPANT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">₹{(p.agreed_cost_inr || 0).toLocaleString("en-IN")}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">₹{(p.paid_amount_inr || 0).toLocaleString("en-IN")}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] uppercase tracking-wide ${
                            p.payment_status === "paid" ? "text-emerald-700" :
                            p.payment_status === "partial" ? "text-amber-700" : "text-rose-600"
                          }`}>{p.payment_status}</span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1 flex-wrap">
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => setShowAddSub(p)} data-testid={`inf-part-submit-${p.id}`}>
                              + Content
                            </Button>
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => setShowAddMetric(p)} data-testid={`inf-part-metric-${p.id}`}>
                              + Metric
                            </Button>
                            {remaining > 0 && (
                              <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => setShowAddPay(p)} data-testid={`inf-part-pay-${p.id}`}>
                                Pay ₹{(remaining / 1000).toFixed(0)}K
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => setShowContract(p)} data-testid={`inf-part-contract-${p.id}`}>
                              <FileSignature className="w-3 h-3 mr-1" /> Contract
                            </Button>
                            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1 text-muted-foreground hover:text-rose-500" onClick={() => removePart(p.id)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                          {deltaMap[p.id] && (
                            <DeltaChips d={deltaMap[p.id]} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "content" && (
        <ContentTab submissions={submissions} participants={participants} onReview={reviewSub} />
      )}

      {tab === "metrics" && (
        <MetricsTab campaignId={cid} participants={participants} onReload={load} />
      )}

      {tab === "payments" && (
        <PaymentsTab payments={payments} />
      )}

      {showAddPart && <AddParticipantDialog cid={cid} wsId={campaign.workspace_id} onClose={() => setShowAddPart(false)} onSaved={() => { setShowAddPart(false); load(); }} />}
      {showAddPay && <AddPaymentDialog cid={cid} participant={showAddPay} onClose={() => setShowAddPay(null)} onSaved={() => { setShowAddPay(null); load(); }} />}
      {showAddSub && <AddSubmissionDialog cid={cid} participant={showAddSub} onClose={() => setShowAddSub(null)} onSaved={() => { setShowAddSub(null); load(); }} />}
      {showAddMetric && <AddMetricDialog cid={cid} participant={showAddMetric} onClose={() => setShowAddMetric(null)} onSaved={() => { setShowAddMetric(null); load(); }} />}
      {showAiBrief && <AiBriefDialog cid={cid} onClose={() => setShowAiBrief(false)} onApplied={() => { setShowAiBrief(false); load(); }} />}
      {showContract && <ContractDialog wsId={campaign.workspace_id} participant={showContract} onClose={() => setShowContract(null)} />}
    </div>
  );
}

function Kpi({ label, value, tone }) {
  return (
    <div className={`border p-3 ${tone === "pos" ? "border-emerald-400 bg-emerald-50/50" : "border-border"}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-xl font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}

function ContentTab({ submissions, participants, onReview }) {
  const pmap = useMemo(() => Object.fromEntries(participants.map((p) => [p.id, p])), [participants]);
  return (
    <div className="space-y-3" data-testid="inf-camp-content-tab">
      {submissions.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground">
          No content submitted yet. Use the "+ Content" button on any creator row to attach a draft link or file.
        </div>
      ) : (
        submissions.map((s) => {
          const p = pmap[s.participant_id];
          const inf = p?.influencer || {};
          return (
            <div key={s.id} className="border border-border p-3" data-testid={`inf-sub-${s.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <div className="text-xs font-semibold">{inf.name || "?"}</div>
                  <div className="text-[10px] text-muted-foreground">@{inf.primary_handle} · v{s.version_no} · {s.kind}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 border ${
                  s.status === "approved" ? "border-emerald-400 text-emerald-700 bg-emerald-50" :
                  s.status === "changes_requested" ? "border-amber-400 text-amber-700 bg-amber-50" :
                  s.status === "published" ? "border-primary/40 text-primary bg-primary/10" :
                  "border-border text-muted-foreground"
                }`}>{s.status.replace("_", " ")}</span>
              </div>
              {s.url && (
                <a href={s.url} target="_blank" rel="noreferrer" className="text-primary text-xs inline-flex items-center gap-1 mt-2 hover:underline">
                  {s.url} <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {s.caption && <div className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap">{s.caption}</div>}
              {s.review_notes && (
                <div className="text-[11px] mt-2 border-t border-border pt-2">
                  <b>Review notes:</b> {s.review_notes}
                </div>
              )}
              {s.status === "submitted" && (
                <div className="flex items-center gap-2 mt-3">
                  <Button size="sm" onClick={() => onReview(s.id, "approved")} className="bg-emerald-600 hover:bg-emerald-500 text-white h-7 text-[11px]" data-testid={`inf-sub-approve-${s.id}`}>
                    <Check className="w-3 h-3 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onReview(s.id, "changes_requested")} className="h-7 text-[11px]" data-testid={`inf-sub-changes-${s.id}`}>
                    <RotateCw className="w-3 h-3 mr-1" /> Request changes
                  </Button>
                </div>
              )}
              {s.status === "approved" && (
                <Button size="sm" onClick={() => onReview(s.id, "published")} className="h-7 text-[11px] mt-2" data-testid={`inf-sub-publish-${s.id}`}>
                  Mark published
                </Button>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

function MetricsTab({ campaignId, participants, onReload }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/influencer-campaigns/${campaignId}/metrics`).then((r) => setRows(r.data?.rows || [])).catch(() => {
      // Fetch via participants aggregated — the summary endpoint has totals.
      setRows([]);
    });
  }, [campaignId]);
  const pmap = useMemo(() => Object.fromEntries(participants.map((p) => [p.id, p])), [participants]);
  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground">
        Enter per-post metrics using the "+ Metric" button next to each creator. The campaign summary above rolls
        them up automatically.
      </div>
      {rows.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground">No metrics recorded yet.</div>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 border-b border-border">
              <tr className="text-left text-[10px] uppercase text-muted-foreground">
                <th className="px-3 py-2">Creator</th>
                <th className="px-3 py-2">Post</th>
                <th className="px-3 py-2 text-right">Reach</th>
                <th className="px-3 py-2 text-right">Interactions</th>
                <th className="px-3 py-2 text-right">ER %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => {
                const p = pmap[r.participant_id];
                const inf = p?.influencer || {};
                const inter = (r.likes || 0) + (r.comments || 0) + (r.saves || 0) + (r.shares || 0);
                return (
                  <tr key={r.id}>
                    <td className="px-3 py-2">{inf.name}</td>
                    <td className="px-3 py-2"><a href={r.post_url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-[11px]">{r.platform || "post"}</a></td>
                    <td className="px-3 py-2 text-right font-mono">{(r.reach || 0).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right font-mono">{inter.toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.er_pct || 0}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PaymentsTab({ payments }) {
  return payments.length === 0 ? (
    <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground">No payments recorded yet.</div>
  ) : (
    <div className="border border-border overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-muted/40 border-b border-border">
          <tr className="text-left text-[10px] uppercase text-muted-foreground">
            <th className="px-3 py-2">Date</th>
            <th className="px-3 py-2 text-right">Amount</th>
            <th className="px-3 py-2">Method</th>
            <th className="px-3 py-2">Invoice</th>
            <th className="px-3 py-2">Recorded by</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {payments.map((p) => (
            <tr key={p.id}>
              <td className="px-3 py-2 font-mono">{p.paid_on}</td>
              <td className="px-3 py-2 text-right font-mono">₹{p.amount_inr.toLocaleString("en-IN")}</td>
              <td className="px-3 py-2">{p.method || "—"}</td>
              <td className="px-3 py-2 font-mono">{p.invoice_no || "—"}</td>
              <td className="px-3 py-2 text-muted-foreground">{p.recorded_by_name}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── DIALOGS ─────────────────────────────────────────────────────
function AddParticipantDialog({ cid, wsId, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [cost, setCost] = useState("");
  const [saving, setSaving] = useState(false);
  const [outreach, setOutreach] = useState(true);

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/influencers?limit=25${q ? `&q=${encodeURIComponent(q)}` : ""}`)
      .then((r) => setResults(r.data?.rows || []))
      .catch(() => setResults([]));
  }, [wsId, q]);

  const add = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.post(`/influencer-campaigns/${cid}/participants`, {
        influencer_id: selected.id,
        agreed_cost_inr: Number(cost) || 0,
        status: "invited",
        create_outreach_thread: outreach,
      });
      toast.success("Added");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="inf-add-part-dialog">
        <DialogHeader><DialogTitle>Add creator to campaign</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search directory…" className="h-9 text-xs" />
          <div className="max-h-60 overflow-y-auto border border-border divide-y divide-border">
            {results.map((r) => (
              <button key={r.id} type="button" onClick={() => setSelected(r)}
                      className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-muted ${selected?.id === r.id ? "bg-primary/10" : ""}`}
                      data-testid={`inf-add-part-pick-${r.id}`}>
                <div className="w-6 h-6 bg-secondary flex items-center justify-center text-[10px] font-bold">{r.name?.[0]}</div>
                <div className="flex-1 min-w-0"><div className="font-medium truncate">{r.name}</div><div className="text-[10px] text-muted-foreground">@{r.primary_handle} · {r.tier}</div></div>
              </button>
            ))}
            {results.length === 0 && <div className="p-3 text-xs text-muted-foreground text-center">No creators found.</div>}
          </div>
          {selected && (
            <>
              <div><Label className="text-[10px] uppercase">Agreed cost (₹)</Label>
                <Input type="number" value={cost} onChange={(e) => setCost(e.target.value)} className="h-9 text-xs"
                       placeholder={selected.rate_card?.per_reel_inr || selected.rate_card?.per_post_inr || 0}
                       data-testid="inf-add-part-cost" />
              </div>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={outreach} onChange={(e) => setOutreach(e.target.checked)} />
                Create Comms Center outreach thread
              </label>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={add} disabled={!selected || saving} data-testid="inf-add-part-save">{saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddPaymentDialog({ cid, participant, onClose, onSaved }) {
  const remaining = Math.max(0, (participant.agreed_cost_inr || 0) - (participant.paid_amount_inr || 0));
  const [amount, setAmount] = useState(String(remaining));
  const [method, setMethod] = useState("bank_transfer");
  const [invoice, setInvoice] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/influencer-campaigns/${cid}/payments`, {
        participant_id: participant.id, amount_inr: Number(amount) || 0,
        method, invoice_no: invoice || undefined,
      });
      toast.success("Payment recorded");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="inf-pay-dialog">
        <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">Outstanding: <b>₹{remaining.toLocaleString("en-IN")}</b></div>
          <div><Label className="text-[10px] uppercase">Amount (₹) *</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-9 text-xs" data-testid="inf-pay-amount" /></div>
          <div><Label className="text-[10px] uppercase">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["bank_transfer", "upi", "cheque", "cash", "other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-[10px] uppercase">Invoice no</Label><Input value={invoice} onChange={(e) => setInvoice(e.target.value)} className="h-9 text-xs" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="inf-pay-save">{saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddSubmissionDialog({ cid, participant, onClose, onSaved }) {
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!url) { toast.error("Content URL required"); return; }
    setSaving(true);
    try {
      await api.post(`/influencer-campaigns/${cid}/submissions`, {
        participant_id: participant.id, kind: "link", url, caption: caption || undefined, notes: notes || undefined,
      });
      toast.success("Submitted");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="inf-sub-dialog">
        <DialogHeader><DialogTitle>Submit content draft</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-[10px] uppercase">Content URL *</Label><Input value={url} onChange={(e) => setUrl(e.target.value)} className="h-9 text-xs" placeholder="https://drive.google.com/…" data-testid="inf-sub-url" /></div>
          <div><Label className="text-[10px] uppercase">Caption</Label>
            <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} className="w-full text-xs px-2 py-1 bg-background border border-border resize-y" />
          </div>
          <div><Label className="text-[10px] uppercase">Notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-9 text-xs" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="inf-sub-save">{saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Submit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddMetricDialog({ cid, participant, onClose, onSaved }) {
  const [state, setState] = useState({
    platform: "instagram", post_url: "",
    reach: "", impressions: "", likes: "", comments: "", saves: "", shares: "",
  });
  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/influencer-campaigns/${cid}/metrics`, {
        participant_id: participant.id,
        platform: state.platform,
        post_url: state.post_url || undefined,
        reach: Number(state.reach) || 0,
        impressions: Number(state.impressions) || 0,
        likes: Number(state.likes) || 0,
        comments: Number(state.comments) || 0,
        saves: Number(state.saves) || 0,
        shares: Number(state.shares) || 0,
        source: "manual",
      });
      toast.success("Metric added");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="inf-metric-dialog">
        <DialogHeader><DialogTitle>Add post metrics</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-[10px] uppercase">Platform</Label>
              <Select value={state.platform} onValueChange={(v) => set("platform", v)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["instagram", "youtube", "tiktok", "linkedin", "twitter", "facebook"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-[10px] uppercase">Post URL</Label><Input value={state.post_url} onChange={(e) => set("post_url", e.target.value)} className="h-9 text-xs" /></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {["reach", "impressions", "likes", "comments", "saves", "shares"].map((k) => (
              <div key={k}><Label className="text-[10px] uppercase">{k}</Label>
                <Input type="number" value={state[k]} onChange={(e) => set(k, e.target.value)} className="h-9 text-xs" data-testid={`inf-metric-${k}`} />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="inf-metric-save">{saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Iter 253z-23 · Delta chips + AI brief + Contracts ───────────────

function DeltaChips({ d }) {
  // Negative delta on CPM/CPE means CHEAPER = good (green).
  // Positive delta on ER means HIGHER engagement = good.
  const chip = (label, val, betterIsLower) => {
    if (val === null || val === undefined) return null;
    const good = betterIsLower ? val < 0 : val > 0;
    const cls = good ? "text-emerald-700 border-emerald-300 bg-emerald-50"
                     : "text-rose-700 border-rose-300 bg-rose-50";
    const sign = val > 0 ? "+" : "";
    return (
      <span key={label} className={`text-[9px] px-1 border ${cls} tabular-nums`}
             title={`${label}: ${val}% vs campaign avg`}>
        {label} {sign}{val}%
      </span>
    );
  };
  return (
    <div className="mt-1 flex flex-wrap gap-1" data-testid="inf-delta-chips">
      {chip("CPM", d.cpm_delta_pct, true)}
      {chip("CPE", d.cpe_delta_pct, true)}
      {chip("ER", d.er_delta_pct, false)}
    </div>
  );
}

function AiBriefDialog({ cid, onClose, onApplied }) {
  const [prompt, setPrompt] = useState("");
  const [provider, setProvider] = useState("claude");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!prompt) { toast.error("Enter a prompt"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/influencer-campaigns/${cid}/ai-brief`, { prompt, provider });
      setResult(data);
      toast.success("Brief generated");
    } catch (e) { toast.error(apiErrorMessage(e, "AI brief failed")); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      await api.post(`/influencer-campaigns/${cid}/ai-brief`, { prompt, provider, apply: true });
      toast.success("Applied to campaign");
      onApplied?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="inf-ai-brief-dialog">
        <DialogHeader><DialogTitle>AI campaign brief generator</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-[10px] uppercase">Prompt</Label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
                      placeholder="e.g. Launch our new cold brew coffee to Mumbai millennials via Reels-heavy creators."
                      className="w-full text-xs px-2 py-1 bg-background border border-border resize-y" data-testid="inf-ai-brief-prompt" />
          </div>
          <div>
            <Label className="text-[10px] uppercase">Model</Label>
            <div className="inline-flex border border-border w-full mt-0.5">
              {["claude", "gpt"].map((p) => (
                <button key={p} type="button" onClick={() => setProvider(p)}
                        className={`flex-1 px-2 py-1.5 text-xs ${provider === p ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}>
                  {p === "claude" ? "Claude Sonnet 4.5" : "GPT 5.2"}
                </button>
              ))}
            </div>
          </div>
          {result && (
            <div className="border border-border p-3 max-h-[40vh] overflow-y-auto space-y-2 text-xs">
              <div><b>Brief:</b> {result.brief}</div>
              {result.mandatory_dos?.length > 0 && <div><b>Dos:</b> {result.mandatory_dos.join(" · ")}</div>}
              {result.mandatory_donts?.length > 0 && <div><b>Don'ts:</b> {result.mandatory_donts.join(" · ")}</div>}
              {result.suggested_hashtags?.length > 0 && <div><b>Hashtags:</b> {result.suggested_hashtags.map((h) => "#" + h).join(" ")}</div>}
              {result.target_reach && <div><b>Target reach:</b> {result.target_reach.toLocaleString("en-IN")}</div>}
              {result.target_engagement_pct && <div><b>Target ER:</b> {result.target_engagement_pct}%</div>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {!result ? (
            <Button onClick={run} disabled={busy || !prompt} data-testid="inf-ai-brief-run">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}Generate
            </Button>
          ) : (
            <Button onClick={apply} disabled={busy} data-testid="inf-ai-brief-apply">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}Apply to campaign
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContractDialog({ wsId, participant, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("default");
  const [contracts, setContracts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);

  const loadAll = useCallback(async () => {
    const [t, c] = await Promise.all([
      api.get(`/workspaces/${wsId}/influencer-contract-templates`).catch(() => ({ data: { rows: [] } })),
      api.get(`/influencer-participants/${participant.id}/contracts`).catch(() => ({ data: { rows: [] } })),
    ]);
    setTemplates(t.data?.rows || []);
    setContracts(c.data?.rows || []);
  }, [wsId, participant.id]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const issue = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/influencer-participants/${participant.id}/contracts`, {
        template_id: templateId,
      });
      setIssued(data);
      toast.success("Contract issued");
      loadAll();
    } catch (e) { toast.error(apiErrorMessage(e, "Issue failed")); }
    finally { setBusy(false); }
  };

  const copyLink = (path) => {
    const url = `${window.location.origin}${path}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied"));
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="inf-contract-dialog">
        <DialogHeader><DialogTitle>Contract — {participant.influencer?.name || participant.influencer_id}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-xs">
          <div>
            <Label className="text-[10px] uppercase">Template</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger className="h-9 text-xs" data-testid="inf-contract-template"><SelectValue /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={issue} disabled={busy} data-testid="inf-contract-issue" className="w-full">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileSignature className="w-3.5 h-3.5 mr-1" />}Issue contract
          </Button>
          {issued && (
            <div className="border border-emerald-400 bg-emerald-50 p-3 space-y-2" data-testid="inf-contract-issued">
              <div className="text-[11px] font-semibold text-emerald-800">Contract issued — share this link with the creator:</div>
              <div className="flex items-center gap-1">
                <code className="text-[10px] flex-1 p-1 bg-background border border-border truncate">{window.location.origin}{issued.public_path}</code>
                <Button size="sm" variant="outline" onClick={() => copyLink(issued.public_path)} className="h-7">
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}
          {contracts.length > 0 && (
            <div className="border border-border">
              <div className="px-2 py-1 bg-muted/40 text-[10px] uppercase font-semibold">Existing contracts</div>
              <div className="divide-y divide-border">
                {contracts.map((c) => (
                  <div key={c.id} className="px-2 py-1.5 text-[11px] flex items-center justify-between">
                    <div>
                      <div>{c.template_name}</div>
                      <div className="text-[10px] text-muted-foreground">issued {c.issued_at?.slice(0, 10)}</div>
                    </div>
                    <span className={`text-[10px] uppercase px-1.5 py-0.5 border ${
                      c.status === "signed" ? "border-emerald-400 text-emerald-700 bg-emerald-50" :
                      c.status === "cancelled" ? "border-rose-400 text-rose-700 bg-rose-50" :
                      "border-amber-400 text-amber-700 bg-amber-50"
                    }`}>{c.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
